import { webSocket } from 'rxjs/webSocket';
import { makeAutoObservable } from 'mobx';
import { PriceDataStore } from './_priceDataStore';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import { TradePlanStore } from './_tradePlanStore';
import { AccountBalanceStore } from './_accountBalanceStore';
import { KillTradingStore } from './_killTradingStore';
import { ApiCallsStore } from './_apiCallsStore';
import { SymbolsLeftStore } from './_symbolsLeftStore';
import { MessageCenterStore } from './_messageCenterStore';
import { SavedTradePlanTemplatesStore } from './_savedTradePlanTemplatesStore';
import { ClosedPositionsStore } from './_closedPositionsStore';
import { ActivityLogsStore } from './_activityLogsStore';
import { WatchlistStore } from './_watchlistStore';

import { handleWebsocketClose } from './utils/handleWebsocketClose';
import { returnWebsocketUrl } from './websocketRoute';
import { LayoutStore } from './_layoutStore';
import { ApplicationTypeStore } from './_applicationTypeStore';
import { UserPreferencesStore } from './_userPreferencesStore';
import { TradierLoginStore } from './_tradierLoginStore';
import { AudioStore } from './_audioStore';
import { UserAuthStore } from './_userAuthStore';


export class WebsocketStore {
  websocketSubject;
  tradePlanWebsocketSubject;
  chartSubject;
  //Dylan some of the following subjects should be turned into stores. one step at a time...
  userPreferencesSubject;
  approxStrikesPerExpirationDateSubject;
  optionExpirationDatesSubject;
  tradeHistorySubject;
  quickTradeDirectionSubject;
  heartBeatSubject;
  soundAlertsSubject;
  importTemplateSubject;
  exportTemplateToHomeSubject;
  exportUserAndTemplateNamesSubject;
  displayDisclaimerAndUpdateHtmlSubject;
  orderPendingSubject;
  optionChainsSubject;
  pushNotificationsSubject;

  //stores
  applicationTypeStore;
  priceDataStore;
  tradePlanStore;
  accountBalanceStore;
  killTradingStore;
  apiCallsStore;
  symbolsLeftStore;
  messageCenterStore;
  closedPositionsStore;
  notificationsStore;
  savedTradePlanTemplatesStore;
  layoutStore;
  userPreferencesStore;
  tradierLoginStore;
  userAuthStore;
  audioStore;
  // alertStore;
  /*
  todo in the current, we are not using this store. You will need it for the option symbols section.
   The store might need some fine tuning, but the concept should work
   */
  watchlistStore;

  connected = false;
  pathRequest = null;

  constructor() {
    makeAutoObservable(this);
    const path = window?.location?.pathname;
    const pathRequest = path === '/ui/alerts' ? 'ALERTS_INTERFACE' : 'TU_INTERFACE';
    this.path = pathRequest;
    const url = returnWebsocketUrl(pathRequest, 'all');
    const tpUrl = returnWebsocketUrl(pathRequest, 'tradePlan');
    this.websocketSubject = this.buildWebsocketSubject(url, path);
    this.tradePlanWebsocketSubject = this.buildWebsocketSubject(tpUrl, path);

    //initialize stores
    this.applicationTypeStore = new ApplicationTypeStore(this);
    this.tradierLoginStore = new TradierLoginStore(this);
    this.userAuthStore = new UserAuthStore(this);
    this.priceDataStore = new PriceDataStore(this);
    this.tradePlanStore = new TradePlanStore(this);
    this.accountBalanceStore = new AccountBalanceStore(this);
    this.killTradingStore = new KillTradingStore(this);
    this.apiCallsStore = new ApiCallsStore(this);
    this.symbolsLeftStore = new SymbolsLeftStore(this);
    this.messageCenterStore = new MessageCenterStore(this);
    this.closedPositionsStore = new ClosedPositionsStore(this);
    this.activityLogsStore = new ActivityLogsStore(this);
    this.savedTradePlanTemplatesStore = new SavedTradePlanTemplatesStore(this);
    this.watchlistStore = new WatchlistStore(this);
    this.layoutStore = new LayoutStore(this);
    this.userPreferencesStore = new UserPreferencesStore(this);
    this.audioStore = new AudioStore(this);
    // this.alertStore = new AlertsStore(this);
    //initialize Subjects

    this.chartSubject = groupByFunction(
      'messageType',
      'getChart',
      this.websocketSubject
    );
    this.userPreferencesSubject = groupByFunction(
      'messageType',
      'userPreferences',
      this.websocketSubject
    );
    this.approxStrikesPerExpirationDateSubject = groupByFunction(
      'messageType',
      'approxStrikesPerExpirationDate',
      this.websocketSubject
    );
    this.optionExpirationDatesSubject = groupByFunction(
      'messageType',
      'optionExpirationDates',
      this.websocketSubject
    );

    this.tradeHistorySubject = groupByFunction(
      'messageType',
      'tradeHistory',
      this.websocketSubject
    );
    this.quickTradeDirectionSubject = groupByFunction(
      'messageType',
      'quickTradeDirection',
      this.websocketSubject
    );
    this.heartBeatSubject = groupByFunction(
      'messageType',
      'heartbeat',
      this.websocketSubject
    );
    this.soundAlertsSubject = groupByFunction(
      'messageType',
      'playSoundAlert',
      this.websocketSubject
    );
    this.importTemplateSubject = groupByFunction(
      'messageType',
      'importTemplate',
      this.websocketSubject
    );
    this.exportTemplateToHomeSubject = groupByFunction(
      'messageType',
      'exportTemplateToHomeResponse',
      this.websocketSubject
    );
    this.exportUserAndTemplateNamesSubject = groupByFunction(
      'messageType',
      'existingExports',
      this.websocketSubject
    );
    this.displayDisclaimerAndUpdateHtmlSubject = groupByFunction(
      'messageType',
      'disclaimer',
      this.websocketSubject
    );
    this.orderPendingSubject = groupByFunction(
      'messageType',
      'orderPendingMonitor',
      this.websocketSubject
    );
    this.optionChainsSubject = groupByFunction(
      'messageType',
      'requestOptionChains',
      this.websocketSubject
    );
    this.pushNotificationsSubject = groupByFunction(
      'messageType',
      'pushNotification',
      this.websocketSubject
    );
  }
  handleConnected = () => {
    console.log('connection ok');
    this.connected = true;
  }
  sendWebsocketMessage = (msg) => {
    const path = window?.location?.pathname;
    const type = path === '/ui/alerts' ? 'ALERTS_APP' : 'TU_APP';
    msg.application = type;
    this.websocketSubject.next(msg);
  };

  buildWebsocketSubject(url, currentPath) {  //variable must be called "url"
    return webSocket({
      url,
      openObserver: {
        next: () => {
          this.handleConnected()
        },
      },
      closeObserver: {
        next(closeEvent) {
          console.log(closeEvent);
          handleWebsocketClose(closeEvent, currentPath);
          this.connected = false;
        },
      },
      deserializer: ({ data }) => {
        try {
          return JSON.parse(data);
        } catch (e) {
          return {};
        }
      },
      serializer: (msg) => {
        if (ArrayBuffer.isView(msg)) {
          return msg;
        }
        return JSON.stringify(msg);
      },
    });
  }

}

